import React, { useEffect } from "react";
import {
  Box,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Autocomplete,
  TextField,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

export default function BucketSelector({ buckets = [], bucket, setBucket }) {
  const [dialogOpen, setDialogOpen] = React.useState(false);

  useEffect(() => {
    if (buckets.length === 0) return;
    if (!bucket || !buckets.includes(bucket)) {
      setBucket(buckets[0]);
    }
  }, [buckets, bucket, setBucket]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          mb: 1,
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        {bucket ? (
          <Tooltip title={bucket} arrow>
            <Chip
              label={bucket}
              color="primary"
              sx={{
                fontWeight: 700,
                fontSize: 16,
                px: 2,
                py: 1,
                borderRadius: 2,
                maxWidth: 340,
              }}
              variant="filled"
            />
          </Tooltip>
        ) : (
          <Chip
            label={
              buckets.length ? "Select a bucket" : "No storage buckets configured"
            }
            variant="outlined"
            sx={{ fontWeight: 600, color: "text.secondary", borderRadius: 2 }}
          />
        )}
        {buckets.length > 1 && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setDialogOpen(true)}
            sx={{ ml: 1, py: 1, borderRadius: 2, fontWeight: 500 }}
          >
            All Buckets ({buckets.length})
          </Button>
        )}
      </Box>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 0,
            boxShadow: 12,
            bgcolor: "background.default",
            minWidth: 360,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            pl: 3,
            pr: 1,
            py: 2,
            bgcolor: "grey.100",
          }}
        >
          <SearchIcon sx={{ mr: 1, color: "primary.main" }} />
          <Typography component="span" sx={{ flex: 1 }}>
            Select Bucket
          </Typography>
          <IconButton onClick={() => setDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3, bgcolor: "background.paper" }}>
          <Autocomplete
            options={buckets}
            value={bucket}
            onChange={(_, value) => {
              setBucket(value);
              setDialogOpen(false);
            }}
            autoHighlight
            getOptionLabel={(option) => option}
            renderOption={(props, option) => (
              <li {...props} key={option}>
                <Tooltip title={option} arrow placement="right">
                  <span
                    style={{
                      display: "inline-block",
                      maxWidth: 260,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: 16,
                    }}
                  >
                    {option}
                  </span>
                </Tooltip>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="All Buckets"
                variant="outlined"
                autoFocus
                sx={{
                  "& .MuiInputBase-root": {
                    fontSize: 18,
                    borderRadius: 2,
                  },
                  mt: 2,
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option === value}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
